import { Card, Text, Flex, Heading, Badge, Box, Button } from '@chakra-ui/react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, useDrawer, useMobileContext, type ConfirmModalHandle } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeta } from '../../api/hooks/useMeta';
import senderNamesService from '../../api/services/senderNamesService';
import SenderNameDrawer from './SenderNameDrawer';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';

const columnHelper = createColumnHelper<SenderName>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SenderNamesPage() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<SenderName>(),
		[],
	);
	const { data: accounts } = useGetAllAccounts();

	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const { meta } = useMeta('senderNamesService');
	const { openDrawer } = useDrawer();

	const confirmRef = useRef<ConfirmModalHandle>(null);

	function getBadgeColorByStatus(status: SenderNameStatus): string {
		switch (status) {
			case 'accepted':
				return 'green';
			case 'rejected':
				return 'red';
			default:
				return 'yellow';
		}
	}

	function getStatusTranslation(status: SenderNameStatus): string {
		switch (status) {
			case 'accepted':
				return t('general.accepted');
			case 'rejected':
				return t('general.rejected');
			case 'processingProspectum':
				return t('general.processing_prospectum');
			case 'processingMyLink':
				return t('general.processing_my_link');
			default:
				return t('general.unknown');
		}
	}

	const columns = [
		columnHelper.accessor('senderNameID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('account.name', {
			header: t('general.account'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return accounts?.data.accounts.find(a => a.accountID === row.original.accountID)?.name || '-';
			},
		}),
		columnHelper.accessor('status', {
			header: t('general.status'),
			cell: ({ row }) => (
				<Badge size="md" colorPalette={getBadgeColorByStatus(row.original.status)}>
					{getStatusTranslation(row.original.status)}
				</Badge>
			),
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: Object.keys((meta?.status as unknown as string) || {}).map(key => ({
					value: key as SenderNameStatus,
					label: (meta?.status?.[key] as string) || key,
				})),
			},
		}),
		columnHelper.accessor('primaryPurpose', {
			header: t('general.primary_purpose'),
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: Object.keys((meta?.primaryPurpose as unknown as string) || {}).map(key => ({
					value: key as SenderNameStatus,
					label: (meta?.primaryPurpose?.[key] as string) || key,
				})),
			},
		}),
		columnHelper.accessor('secondaryPurpose', {
			header: t('general.secondary_purpose'),
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: Object.keys((meta?.secondaryPurpose as unknown as string) || {}).map(key => ({
					value: key as SenderNameStatus,
					label: (meta?.secondaryPurpose?.[key] as string) || key,
				})),
			},
		}),
		columnHelper.accessor('tertiaryPurpose', {
			header: t('general.tertiary_purpose'),
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: Object.keys((meta?.tertiaryPurpose as unknown as string) || {}).map(key => ({
					value: key as SenderNameStatus,
					label: (meta?.tertiaryPurpose?.[key] as string) || key,
				})),
			},
		}),
		columnHelper.accessor('companyName', {
			header: t('general.company_name'),
		}),
		columnHelper.accessor('contactPersonName', {
			header: t('general.contact_person_name'),
		}),
		columnHelper.accessor('contactEmail', {
			header: t('general.contact_email'),
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			cell: ({ row }) => dbTimeToUserTime(row.original.timeCreated, TIMEZONE),
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			cell: ({ row }) => (row.original.timeModified ? dbTimeToUserTime(row.original.timeModified, TIMEZONE) : null),
		}),
	];

	return (
		<Flex flexDirection="column" gap={5} width="100%" alignItems="center" height="100%">
			{!isMobile && (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.sender_names')}</Heading>
						<Text>{t('general.sender_names_guide')}</Text>
					</Card.Body>
				</Card.Root>
			)}
			<Flex overflow="auto" width="100%" display="flex" flexDirection="column" height="100%" maxW="2000px">
				<Flex display="flex" flex={1} overflow="auto">
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="senderNames"
								keyAttribute="senderNameID"
								emptyDataText={t('general.no_sender_names_found')}
								fetchFn={queryParams =>
									senderNamesService.getAllResources({
										...queryParams,
										getRelated: 'account',
									})
								}
								initialItemsPerPage={25}
								columns={columns}
								isSelectable={false}
								defaultHiddenColumns={[]}
								isServerSide={true}
								defaultSort={{ id: 'timeCreated', desc: true }}
								onClickRow={row => {
									openDrawer({
										id: 'senderNameDrawer',
										content: <SenderNameDrawer senderNameID={row.original.senderNameID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
									<DataCollectionTableHeader />
									<Button
										id="addSenderName"
										onClick={() => {
											openDrawer({
												id: 'senderNameDrawer',
												content: <SenderNameDrawer />,
											});
										}}
									>
										{t('general.add_sender_name')}
									</Button>
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
									<ConfirmModal ref={confirmRef} />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
